"use client";

import { AlertTriangle, CheckCircle2, ClipboardPlus, LoaderCircle, Sparkles, X } from "lucide-react";
import type { TemplatePreview } from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type TemplateModalProps = {
  template: TemplatePreview | null;
  loading: boolean;
  onClose: () => void;
  onCommit: (template: TemplatePreview) => void;
};

export function TemplateModal({ template, loading, onClose, onCommit }: TemplateModalProps) {
  const { t } = useLang();
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="template-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="template-head">
          <div className="template-mark"><ClipboardPlus size={21} /></div>
          <div>
            <span>{t("tpl.preview")}</span>
            <h2 id="template-title">{loading ? t("tpl.reviewing") : template?.name}</h2>
            <p>{template?.subtitle ?? t("tpl.subtitle.fallback")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close template preview"><X size={19} /></button>
        </div>

        {loading || !template ? (
          <div className="template-loading"><LoaderCircle className="spin" size={26} /> {t("tpl.loading")}</div>
        ) : (
          <>
            <div className="template-columns">
              <div>
                <h3><Sparkles size={14} /> {t("tpl.soapSkeleton")}</h3>
                <div className="pending-list">
                  {Object.entries(template.soap).map(([field, value]) => (
                    <div className="pending-item" key={field}>
                      <span>{field.slice(0, 1).toUpperCase()}</span>
                      <p>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3><ClipboardPlus size={14} /> {t("tpl.pendingOrders")}</h3>
                <div className="pending-list">
                  {template.orders.map((order) => (
                    <div className={`pending-item ${order.conflict ? "conflict" : ""}`} key={order.id}>
                      <span>{order.conflict ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}</span>
                      <div>
                        <strong>{order.label}</strong>
                        <p>{order.details} · {order.lane === "one-day" ? t("order.oneDay") : t("order.continuous")}</p>
                        {order.conflict && <em>{order.conflict} — {t("tpl.excluded")}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="template-alert">
              <AlertTriangle size={17} />
              <div><strong>{t("tpl.conflictTitle")}</strong><span>{t("tpl.conflictBody")}</span></div>
            </div>
            <div className="template-actions">
              <button type="button" className="secondary-button" onClick={onClose}>{t("common.cancel")}</button>
              <button type="button" className="primary-button" onClick={() => onCommit(template)}>
                <CheckCircle2 size={16} /> {t("tpl.commit")}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
